// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from './ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { TradingMode } from '@shared/utils/Instruments/TradingMode';
import { TradingPositionType } from '@shared/utils/Instruments/TradingPositionType';
import { SessionOperations } from '@shared/utils/Enums/Constants';
import { OrderTif } from '@shared/utils/Trading/OrderTifEnum';
import { OrderStatus } from '@shared/utils/Trading/OrderStatus';
import { AccountTradeStatus } from '@shared/utils/Account/AccountTradeStatus';
import { AccountType } from '@shared/utils/Account/AccountType';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { ExternalOrderStatus } from '@shared/utils/Trading/ExternalOrderStatus';
import { RiskPlan } from './cache/RiskPlan';
import { Instrument } from './cache/Instrument';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { ApplicationInfo } from './ApplicationInfo';
import { IsAllowedResponceReason, IsAllowedResponceReasonName } from './IsAllowedResponceReason';
import { BaseSettings } from './Settings/BaseGeneralSettingsWrapper';
import { ClientType } from '@shared/utils/ClientType';
let DataCache = null;
export class IsAllowed {
    constructor(dataCache) {
        DataCache = dataCache;
    }
    static IsCloseAllAllowed(isPosition, isUseLockTrading = true) {
        try {
            if (ApplicationInfo.isExploreMode) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
            }
            if (!DataCache.Loaded) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotLoadCache);
            }
            const IsCheckTradingNotLocked = isUseLockTrading ? !TradingLockUtils.TradingLock.tradingLocked : true; // не проверяем залочена ли торговля, если isUseLockTrading = false;
            if (TradingLockUtils.TradingLock.LockTradingByPassword) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
            }
            if (!IsCheckTradingNotLocked) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
            }
            if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
            }
            if (DataCache.isAllowedForMyUser(RulesSet.TERMINAL_HTML)) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleAdministration);
            }
            if (DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_POSITIONSADMIN)) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.PositionsAdmin);
            }
            if (isPosition) {
                // +++yura - проверка рула "RulesSet.FUNCTION_CLOSE_POSITIONS_DISABLE" по рулам аккаунта, а не по всем рулам
                const activeAccount = DataCache.ActiveUser != null ? DataCache.ActiveUser.Account : null;
                let isClosePositionsDisable = true;
                if (activeAccount != null) {
                    isClosePositionsDisable = DataCache.isAllowedForAccount(RulesSet.FUNCTION_CLOSE_POSITIONS_DISABLE, activeAccount);
                }
                else {
                    isClosePositionsDisable = DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_CLOSE_POSITIONS_DISABLE);
                }
                if (isClosePositionsDisable) {
                    return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleClosePositionDisable);
                }
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.Unknown);
        }
        // TODO
        return IsAllowedResponce.AllowedResponce();
    }
    static IsRollbackAllowed() {
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        // TODO
        return IsAllowedResponce.AllowedResponce();
    }
    static isOCOAllowedForAccount(acc) {
        const isAllowed = DataCache.isAllowedForAccount(RulesSet.FUNCTION_BINDEDORDERS, acc);
        const isActiveStatus = acc.AccountTradeStatus === AccountTradeStatus.ACTIVE_STATUS ||
            acc.AccountTradeStatus === AccountTradeStatus.LIQUIDATION_ONLY_STATUS;
        return isAllowed && isActiveStatus;
    }
    static IsAccountsAllowOCO(accounts) {
        for (const account of accounts) {
            if (!this.isOCOAllowedForAccount(account)) {
                return false;
            }
        }
        return true;
    }
    static IsAnyAccountAllowOCO() {
        const accounts = DataCache.getAccounts();
        for (const key in accounts) {
            if (this.isOCOAllowedForAccount(accounts[key])) {
                return true;
            }
        }
        return false;
    }
    static IsPositionClosingAllowed(pos, isMarketClose) {
        // let CurrentRule = IsAllowedRule.IsPositionClosingAllowed;
        if (pos == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        if (pos.IsPendingExerciseOptionStatus()) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OptionPendingExerciseStatus);
        }
        // 106414
        if (pos.ProductType === ProductType.CorporateAction) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedCorporateActionPositions);
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_TRADING" rule
        if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        /// / Analyze "FUNCTION_TRADING" rule  ++ AccountTradeStatus
        if (!IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        //
        if (!IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        // Analyze instrument trading mode: open or closed
        const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(pos.Instrument, false);
        if (!instrumentTradingMode.Allowed) {
            return instrumentTradingMode;
        }
        if (isMarketClose && !IsAllowed.Check_Session_OrderType(pos.Instrument, OrderType.Market)) {
            // https://tp.traderevolution.com/entity/87716
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.MarketCloseNotAllowed);
        }
        if (!isMarketClose && pos.Instrument.isHedgingNettingType()) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.CloseViaOENotAllowed);
        }
        // #118545
        // I don't see a similar check in the corresponding method of the desktop application,
        // which could potentially lead to a bug like the one described in ticket https://tp.traderevolution.com/entity/125204.
        // Please ensure that the CurrentSessionPeriod is properly validated to prevent any issues.
        // I didn't remove this if to avoid breaking https://tp.traderevolution.com/entity/118545
        if (pos.Instrument.Session.BlockTrading || pos.Instrument.Session.CurrentSessionPeriod === null) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        return IsAllowedResponce.AllowedResponce();
    }
    static IsOrderCancelingAllowed(order, ignoreLockTrading = false, ignoreCheckByAccount = false, ignoreCheckBySymbol = false) {
        // CurrentRule = IsAllowedRule.IsOrderCancelingAllowed;
        if (order == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (!ignoreLockTrading && TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_TRADING" rule
        if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        // Analyze "FUNCTION_TRADING" rule  ++ AccountTradeStatus
        if (!ignoreCheckByAccount && !IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        if (!ignoreCheckByAccount && !IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        // Analyze instrument trading mode: open or closed
        if (!ignoreCheckBySymbol) {
            const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(order.Instrument, true);
            if (!instrumentTradingMode.Allowed) {
                return instrumentTradingMode;
            }
        }
        // Analyze instrument delivery method and account type
        if (!ignoreCheckBySymbol &&
            !ignoreCheckByAccount &&
            !IsAllowed.Check_Instrument_For_Account_Delivery(order.Instrument, order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentForAccountDelivery);
        }
        // Session - operation Cancel
        if (!ignoreCheckBySymbol &&
            !IsAllowed.Check_Session_Operation(order.Account, order.Instrument, SessionOperations.Cancel) &&
            order.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // Check route status
        if (!ignoreCheckBySymbol &&
            !IsAllowed.Check_RouteRunning(order.Account, order.Instrument) &&
            order.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RouteNotRunning);
        }
        // PENDING_NEW
        if (!IsAllowed.Check_OrderStatus(order)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByOrderStaus);
        }
        if (order.Instrument.Session.BlockTrading) {
            // #118545
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // Allowed
        return IsAllowedResponce.AllowedResponce();
    }
    static IsPositionModifyingAllowed(pos) {
        // CurrentRule = IsAllowedRule.IsPositionModifyingAllowed;
        if (pos == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_TRADING" rule
        if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        // Analyze "FUNCTION_TRADING" rule  ++ AccountTradeStatus
        if (!IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        if (!IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        // Analyze instrument trading mode: open or closed
        const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(pos.Instrument, false);
        if (!instrumentTradingMode.Allowed) {
            return instrumentTradingMode;
        }
        // // Session - operation modify
        if (!IsAllowed.Check_Session_Operation(pos.Account, pos.Instrument, SessionOperations.Modify)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // Check "FUNCTION_SLTP" rule
        if (!DataCache.isAllowedForAccount(RulesSet.FUNCTION_SLTP, pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleSlTpNotAllowed);
        }
        // Route
        if (!IsAllowed.IsSLTPAllowed(pos.Instrument, pos.Account, true).Allowed &&
            !IsAllowed.IsSLTPAllowed(pos.Instrument, pos.Account, false).Allowed) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedRouteOrderType);
        }
        if (pos.SuperPositionId !== '-1' && !pos.isSuperPosition) {
            // Cant modify FIFO sub position #80010
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (pos.IsPendingExerciseOptionStatus()) {
            // #93127 Изменения на клиенте связанные со статусом Pending exercise: В контекстном меню блокируем возможность: Modify position, Modify SL to breakevent, Quick SL/TP,  Close position
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OptionPendingExerciseStatus);
        }
        if (pos.ProductType === ProductType.CorporateAction) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedCorporateActionPositions);
        }
        if (IsAllowed.IsMaxLotHasBeenReached(pos)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedMaxLotHasBeenReached);
        }
        // #118545
        // Attention! CurrentSessionPeriod may be null if today is a holiday (as per ticket https://tp.traderevolution.com/entity/125204).
        if (pos.Instrument.Session.BlockTrading || pos.Instrument.Session.CurrentSessionPeriod === null) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // Allowed
        return IsAllowedResponce.AllowedResponce();
    }
    static RouteAllowModifyTIF(instrument) {
        var _a;
        if (!instrument) {
            return false;
        }
        return !!((_a = DataCache.Routes.getRouteByName(instrument.Route)) === null || _a === void 0 ? void 0 : _a.AllowTifModify);
    }
    static IsPositionProductTypeModifyingAllowedForPosition(position) {
        if (!position) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        const pos = position;
        // Пункт должен быть задисейблен, если выбрана SuperPosition позиция.
        if (pos.isSuperPosition) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        const instrument = pos.Instrument;
        if (!instrument) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        const allowedProductType = InstrumentUtils.getAllowedProductTypeDict(instrument);
        // Пункт должен быть задисейблен, если для выбранной Position доступен только один Product type.
        if (allowedProductType.length <= 1) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        return IsAllowed.IsModifyProductTypeAllowed(pos);
    }
    static IsPositionProductTypeModifyingAllowed(positions) {
        var _a;
        // Пункт должен быть задисейблен, если выбрано несколько Positions.
        if (positions.length !== 1) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        return IsAllowed.IsPositionProductTypeModifyingAllowedForPosition((_a = positions[0]) !== null && _a !== void 0 ? _a : null);
    }
    static IsPositionExerciseOptionAllowed(positions) {
        // #93127 метод возвращает видимость пункта контекстного меню панели Positions
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        for (const pos of positions) {
            if (!pos || pos.IsPendingExerciseOptionStatus()) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OptionPendingExerciseStatus);
            }
            if (pos.isTradePosition) {
                // 93127: Для позиций с типом неттинга FIFO пункт Exercise position должен появляться только для Суперпозиции
                return IsAllowedResponce.NotAllowedResponce();
            }
            const instrument = pos.Instrument;
            if (!instrument) {
                return IsAllowedResponce.NotAllowedResponce();
            }
            if (!instrument.IsAmericanExerciseStyle() && !instrument.AllowOptionsExerciseOnLastTradeDate()) {
                // Пункт отображается и не активен, если в панели выбрано несколько позиций по опционам с Exercise style = American.
                return IsAllowedResponce.NotAllowedResponce();
            }
        }
        return positions.length > 0
            ? IsAllowedResponce.AllowedResponce()
            : IsAllowedResponce.NotAllowedResponce( /* ? */);
    }
    static IsPositionExerciseCancelAllowed(positions) {
        var _a;
        // #93127 метод возвращает enabled пункта контекстного меню панели Positions
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        if (!IsAllowed.IsPositionExerciseCancelVisible(positions).Allowed) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (positions.length !== 1) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.MoreThanOnePosSelected);
        }
        const pos = (_a = positions[0]) !== null && _a !== void 0 ? _a : null;
        if (isNullOrUndefined(pos)) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (isNullOrUndefined(pos.Instrument) ||
            !IsAllowed.Check_Session_Operation(pos.Account, pos.Instrument, SessionOperations.Open)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OpenNotAllowedInTrSession);
        }
        return positions.length > 0
            ? IsAllowedResponce.AllowedResponce()
            : IsAllowedResponce.NotAllowedResponce( /* ? */);
    }
    static IsPositionExerciseCancelVisible(positions) {
        // #93127 метод возвращает видимость пункта контекстного меню панели Positions
        for (const pos of positions) {
            if (!pos || pos.isTradePosition || !pos.IsPendingExerciseOptionStatus()) {
                // на самом деле тут причина в том что НЕ OptionPendingExerciseStatus но за ненадобностью пока не создавал новый идентификатор причины
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OptionPendingExerciseStatus);
            }
            const instrument = pos.Instrument;
            if (!instrument) {
                return IsAllowedResponce.NotAllowedResponce();
            }
            if (!instrument.IsAmericanExerciseStyle() && !instrument.AllowOptionsExerciseOnLastTradeDate()) {
                return IsAllowedResponce.NotAllowedResponce();
            }
        }
        return positions.length > 0
            ? IsAllowedResponce.AllowedResponce()
            : IsAllowedResponce.NotAllowedResponce( /* ? */);
    }
    static IsPositionsReversingAllowed(positions, checkAccountByAdmin = false) {
        let atLeastOnePositionPresent = false;
        for (const position of positions) {
            atLeastOnePositionPresent = true;
            const allowedResp = IsAllowed.IsPositionReversingAllowed(position, false, checkAccountByAdmin);
            if (!allowedResp.Allowed) {
                return allowedResp;
            }
        }
        return atLeastOnePositionPresent ? IsAllowedResponce.AllowedResponce() : IsAllowedResponce.NotAllowedResponce();
    }
    static IsPositionReversingAllowed(pos, ignoreCheckByAccount = false, checAccountByAdmin = false) {
        // Проверить доступность реверса позиции
        // CurrentRule = IsAllowedRule.IsPositionReversingAllowed;
        if (pos == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (pos.isTradePosition) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedForTrades);
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        if (checAccountByAdmin /* !Utils.RunByAutotest && */ &&
            DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_POSITIONSADMIN) === true) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.PositionsAdmin);
        }
        if (TradingLockUtils.TradingLock.tradingLocked) {
            // Analyze "LOCK_TRADING"
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING) === false) {
            // Analyze "FUNCTION_TRADING" rule
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        const route = DataCache.Routes.getRouteById(pos.Instrument.getRoute());
        if (!(route.IsAllowableTif(OrderTif.Day, OrderType.Market) ||
            route.IsAllowableTif(OrderTif.GTC, OrderType.Market) ||
            route.IsAllowableTif(OrderTif.IOC, OrderType.Market) ||
            route.IsAllowableTif(OrderTif.GTD, OrderType.Market))) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotSupportedTIF);
        }
        if (!ignoreCheckByAccount && !IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(pos.Account)) {
            // Analyze "FUNCTION_TRADING" rule
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        if (!ignoreCheckByAccount && !IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(pos.Account)) {
            // Analyze "FUNCTION_SELF_TRADING_Rule" rule
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(pos.Instrument, false);
        if (!instrumentTradingMode.Allowed) {
            // Analyze instrument trading mode: open or closed
            return instrumentTradingMode;
        }
        if (!IsAllowed.Check_Last_Quote(pos.Instrument, OrderType.Market, true)) {
            // Last Quote: for Market order Type trading not allowed without quotes
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NoLastQuote);
        }
        if (!IsAllowed.Check_Session_OrderType(pos.Instrument, OrderType.Market)) {
            // Session - Market OrderType
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OrderTypeNotAllowedBySession);
        }
        if (!IsAllowed.Check_RouteRunning(pos.Account, pos.Instrument)) {
            // Check route status
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RouteNotRunning);
        }
        if (!ignoreCheckByAccount &&
            !IsAllowed.Check_Instrument_For_Account_Visibility([pos.Account], pos.Instrument)) {
            // Check instrument/route visibility for account
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotVisibleInstrumentRoute);
        }
        if (!ignoreCheckByAccount &&
            DataCache.isAllowedForAccount(RulesSet.FUNCTION_CLOSE_POSITIONS_DISABLE, pos.Account) === true) {
            // Check "FUNCTION_CLOSE_POSITIONS_DISABLE" rule
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleClosePositionDisable);
        }
        if (pos.Instrument.isMultiPositionPerSide()) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.WrongTraidingPositionType);
        }
        if (pos.IsPendingExerciseOptionStatus()) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OptionPendingExerciseStatus);
        }
        if (pos.ProductType === ProductType.CorporateAction) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedCorporateActionPositions);
        }
        return IsAllowedResponce.AllowedResponce();
    }
    static IsOrderModifyingAllowed(order) {
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        // CurrentRule = IsAllowedRule.IsOrderModifyingAllowed;
        if (order == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_TRADING" rule
        if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        // // Analyze "FUNCTION_TRADING" rule  ++ AccountTradeStatus
        if (!IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        if (!IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        // Analyze instrument trading mode: open or closed
        const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(order.Instrument, false);
        if (!instrumentTradingMode.Allowed) {
            return instrumentTradingMode;
        }
        // Analyze instrument delivery method and account type
        if (!IsAllowed.Check_Instrument_For_Account_Delivery(order.Instrument, order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentForAccountDelivery);
        }
        // Session - operation modify
        if (!IsAllowed.Check_Session_Operation(order.Account, order.Instrument, SessionOperations.Modify) &&
            order.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // // Check route status
        if (!IsAllowed.Check_RouteRunning(order.Account, order.Instrument) && order.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RouteNotRunning);
        }
        // Market - modify not allowed but // https://tp.traderevolution.com/entity/88385
        if (order.OrderType === OrderType.Market && !IsAllowed.isOrderStatusAllowMarketOrderModification(order)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedByOrderType);
        }
        if (order.OrderType === OrderType.OCO || (order.BoundTo !== '-1' && order.BoundTo)) {
            if (!IsAllowed.IsAccountsAllowOCO([order.Account])) {
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleOCO);
            }
        }
        // Analyse "Replaceable" for non SLTp orders
        if (!order.DataCache.Trading.IsSLTP(order) && !order.Replaceable) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (order.PositionId !== '-1' && !order.Active) {
            const allowedResult = IsAllowed.IsPositionModifyingAllowed(DataCache.Trading.getPositionById(order.PositionId));
            if (!allowedResult.Allowed) {
                return allowedResult;
            }
        }
        // PENDING_NEW
        if (!IsAllowed.Check_OrderStatus(order)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByOrderStaus);
        }
        // Analyze BlockTradingBySession
        if (!IsAllowed.Check_Session_BlockTrading(order.Instrument)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // #118545
        // Attention! CurrentSessionPeriod may be null if today is a holiday (as per ticket https://tp.traderevolution.com/entity/125204).
        if (!IsAllowed.Check_Session_CurrentPeriod(order)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        if (order.OrderType === OrderType.Care && ClientType.IsWebMobile()) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.ModifyCareOrderWebMobile);
        }
        // #99540
        // No trading blocking when OnlyPositionClosing:
        // https://docs.google.com/document/d/1QTBbM_I0jRQ5H29fJYokuc_Cy7XfiCbWNIOypm6Vn7Q/edit
        // if (order.Instrument.IsPositionCloseOnly(order.ProductType, order.Account) && !isPosition)
        //     return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedPositionCloseOnly);
        // Allowed
        return IsAllowedResponce.AllowedResponce();
    }
    static IsOrderExecutingAllowed(order) {
        var _a;
        // CurrentRule = IsAllowedRule.IsOrderExecutingAllowed;
        if (order == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_TRADING" rule
        if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        // Analyze "FUNCTION_TRADING" rule  ++ AccountTradeStatus
        if (!IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        if (!IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        //  Analyze instrument trading mode: open or closed
        const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(order.Instrument, false);
        if (!instrumentTradingMode.Allowed) {
            return instrumentTradingMode;
        }
        // Analyze instrument delivery method and account type
        if (!IsAllowed.Check_Instrument_For_Account_Delivery(order.Instrument, order.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentForAccountDelivery);
        }
        // Session - operation modify
        if (!IsAllowed.Check_Session_Operation(order.Account, order.Instrument, SessionOperations.Modify) &&
            order.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // // Session - Market OrderType
        if (!IsAllowed.Check_Session_OrderType(order.Instrument, OrderType.Market)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OrderTypeNotAllowedBySession);
        }
        // Analyze BlockTradingBySession
        if (!IsAllowed.Check_Session_BlockTrading(order.Instrument)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // Attention! CurrentSessionPeriod may be null if today is a holiday (as per ticket https://tp.traderevolution.com/entity/125204).
        if (!IsAllowed.Check_Session_CurrentPeriod(order)) {
            // #103032
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        // // Check route status
        if (!IsAllowed.Check_RouteRunning(order.Account, order.Instrument) && order.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RouteNotRunning);
        }
        // // Check instrument/route visibility for account
        if (!IsAllowed.Check_Instrument_For_Account_Visibility([order.Account], order.Instrument)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotVisibleInstrumentRoute);
        }
        // Market - modify not allowed
        if (order.OrderType === OrderType.Market) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedByOrderType);
        }
        // nicky. Если запрещен тип маркет, или запрещено менять тип ордера,
        // скрывается возможность делать экзекьют ас маркет
        const rout = DataCache.Routes.getRouteByName(order.Route);
        if (rout !== null && (!rout.IsAllowableOrderType(OrderType.Market) || !rout.AllowTypeModify)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.ExecuteAtMarketNotAllowedOrderType);
        }
        // SL/TP нельзя выполнить как маркет
        if (order.DataCache.Trading.IsSLTP(order)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.ExecuteAtMarketNotAllowedForSlTp);
        }
        // OCO нельзя выполнить как маркет, надо сначала снять баунд ту
        if (((_a = order.BoundTo) === null || _a === void 0 ? void 0 : _a.length) && order.BoundTo !== '-1') {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.ExecuteAtMarketNotAllowedForBindOrders);
        }
        // PENDING_NEW
        if (!IsAllowed.Check_OrderStatus(order)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByOrderStaus);
        }
        // #99540
        // No trading blocking when OnlyPositionClosing:
        // https://docs.google.com/document/d/1QTBbM_I0jRQ5H29fJYokuc_Cy7XfiCbWNIOypm6Vn7Q/edit
        // if (pos.Instrument.IsPositionCloseOnly(pos.ProductType, pos.Account))
        //     return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedPositionCloseOnly);
        // Allowed
        return IsAllowedResponce.AllowedResponce();
    }
    // TODO.
    static IsTradingAllowed(account, instrument, orderType, sessOperation = undefined) {
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (ApplicationInfo.isExploreMode) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByExploreMode);
        }
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        if (!IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        // Analyze instrument trading mode: open or closed
        if (orderType !== -1) {
            const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(instrument, false);
            if (!instrumentTradingMode.Allowed) {
                return instrumentTradingMode;
            }
        }
        // Analyze instrument delivery method and account type
        if (!IsAllowed.Check_Instrument_For_Account_Delivery(instrument, account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentForAccountDelivery);
        }
        if (!IsAllowed.Check_RouteRunning(account, instrument)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RouteNotRunning);
        }
        // // 123591
        if (!IsAllowed.Check_Session_Operation(account, instrument, isNullOrUndefined(sessOperation) ? SessionOperations.Open : sessOperation)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        }
        if (orderType !== -1 && !IsAllowed.Check_Session_OrderType(instrument, orderType)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OrderTypeNotAllowedBySession);
        }
        if (!account.DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        if (!IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        return IsAllowedResponce.AllowedResponce();
    }
    static IsOrderSLTPAllowed(pos) {
        // CurrentRule = IsAllowedRule.IsOrderSLTPAllowed;
        if (pos == null) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        //
        // Управление SLTP на уровне ордера, используется пока только в интеграциях
        //
        // if (!pos.SLTPAllowed)
        //     return IsAllowedResponce.NotAllowedResponce();
        if (TradingLockUtils.TradingLock.LockTradingByPassword) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTradingByPassword);
        }
        // Analyze "LOCK_TRADING"
        if (TradingLockUtils.TradingLock.tradingLocked) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.LockTrading);
        }
        // Analyze "FUNCTION_TRADING" rule
        if (!DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleTrading);
        }
        // Analyze "FUNCTION_TRADING" rule  ++ AccountTradeStatus
        if (!IsAllowed.Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionTrading);
        }
        //
        // Analyze "FUNCTION_SELF_TRADING_Rule" rule
        //
        if (!IsAllowed.Check_FUNCTION_SELF_TRADING_Rule(pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleFunctionSelfTrading);
        }
        // Analyze instrument trading mode: open or closed
        const instrumentTradingMode = IsAllowed.Check_Instrument_TradingMode(pos.Instrument, false);
        if (!instrumentTradingMode.Allowed) {
            return instrumentTradingMode;
        }
        //
        // Analyze instrument delivery method and account type
        //
        if (!IsAllowed.Check_Instrument_For_Account_Delivery(pos.Instrument, pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentForAccountDelivery);
        }
        // Session - operation modify
        // if (!Check_Session_Operation(pos.instrument, SessionOpeartions.Modify) && !CheckOffMarket(pos.Account))
        //     return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.OperationNotAllowedBySession);
        // Check route status
        if (!IsAllowed.Check_RouteRunning(pos.Account, pos.Instrument) && pos.Status !== OrderStatus.OFF_MARKET) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RouteNotRunning);
        }
        // // Check instrument/route visibility for account
        // if (!Check_Instrument_For_Account_Visibility(new List<Account>(new Account[] { pos.Account }), pos.instrument))
        //     return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotVisibleInstrumentRoute);
        // Check rule "FUNCTION_SLTP"
        if (!pos.DataCache.isAllowedForAccount(RulesSet.FUNCTION_SLTP, pos.Account)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.RuleSlTpNotAllowed);
        }
        //
        // PENDING_NEW
        //
        if (!IsAllowed.Check_OrderStatus(pos)) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedByOrderStaus);
        }
        //
        // Route
        //
        if (!IsAllowed.IsSLTPAllowed(pos.Instrument, pos.Account, true).Allowed &&
            !IsAllowed.IsSLTPAllowed(pos.Instrument, pos.Account, false).Allowed) {
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedRouteOrderType);
        }
        // #99540
        // No trading blocking when OnlyPositionClosing:
        // https://docs.google.com/document/d/1QTBbM_I0jRQ5H29fJYokuc_Cy7XfiCbWNIOypm6Vn7Q/edit
        // if (pos.Instrument.IsPositionCloseOnly(pos.ProductType, pos.Account) && !pos.isPosition)
        //     return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedPositionCloseOnly);
        if (IsAllowed.IsMaxLotHasBeenReached(pos)) {
            // #107427 doc 2.1) https://docs.google.com/document/d/1roxsr6hYqGs_9kqR3S7DUt1PrIYr7CVF4RxT3yLDlxk/edit#heading=h.vv29hgs84duy
            return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.NotAllowedMaxLotHasBeenReached);
        }
        // Allowed
        return IsAllowedResponce.AllowedResponce();
    }
    static isOrderStatusAllowMarketOrderModification(order) {
        // https://tp.traderevolution.com/entity/88385
        const status = order.Status;
        const statuses = OrderStatus;
        return (status === statuses.NEW ||
            status === statuses.PART_FILLED ||
            status === statuses.WAITING_MARKET ||
            status === statuses.OFF_MARKET ||
            status === statuses.UNPLACED);
    }
    static Check_OrderStatus(order) {
        return (order.Status !== OrderStatus.PENDING_NEW &&
            order.Status !== OrderStatus.PENDING_CANCEL &&
            order.Status !== OrderStatus.PENDING_REPLACE &&
            order.Status !== OrderStatus.WAITING_CANCELLATION &&
            order.ExternalStatus !== ExternalOrderStatus.New &&
            order.ExternalStatus !== ExternalOrderStatus.PendingCancel &&
            order.ExternalStatus !== ExternalOrderStatus.PendingModification);
    }
    // Source C# Utils.IsAllowedSlTp
    static IsSLTPAllowed(instrument, account, isSL) {
        try {
            if (isNullOrUndefined(instrument)) {
                return IsAllowedResponce.NotAllowedResponce();
            }
            if (IsAllowed.IsProtectiveOrders()) {
                // #108472 <- 108200
                return IsAllowedResponce.AllowedResponce();
            }
            // Отключаем SLTP если правило не разрешает
            let isEnable = false;
            if (!isNullOrUndefined(account)) {
                // #32868 скрыть Sl/Tp для мультивалютных аккаунтов
                isEnable =
                    account.AccountType !== AccountType.MultiAsset &&
                        DataCache.isAllowedForAccount(RulesSet.FUNCTION_SLTP, account);
            }
            else {
                isEnable = DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_SLTP);
            }
            if (!isEnable) {
                return IsAllowedResponce.NotAllowedResponce();
            }
            const route = DataCache.Routes.getRouteByName(instrument.getRoute());
            // #31120 (Trading) выставление Sl/Tp/TR stop
            let isSlSuportedByTif = false;
            let isTpSuportedByTif = false;
            const stopOrderType = OrderType[BaseSettings.isUseStopLimitInsteadStop() ? 'StopLimit' : 'Stop']; // #91382
            const tifs = OrderTif;
            if (route !== null) {
                for (const key in tifs) {
                    if (route.IsAllowableTif(tifs[key], stopOrderType)) {
                        isSlSuportedByTif = true;
                    }
                    if (route.IsAllowableTif(tifs[key], OrderType.Limit)) {
                        isTpSuportedByTif = true;
                    }
                }
            }
            let resBool = true;
            if (isSL) {
                resBool = isSlSuportedByTif && IsAllowed.Check_Session_OrderType(instrument, stopOrderType);
            }
            else {
                resBool = isTpSuportedByTif && IsAllowed.Check_Session_OrderType(instrument, OrderType.Limit);
            }
            return isEnable && resBool ? IsAllowedResponce.AllowedResponce() : IsAllowedResponce.NotAllowedResponce();
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
            return IsAllowedResponce.NotAllowedResponce();
        }
    }
    // Source C# AdvancedOE.IsAllowableTS_ForSL
    // проверка доступности TrailingStop для StopLoss
    static IsTrailingStopForSLAllowed(instrument, account, isPosition = false) {
        // #31120 - на смену тифа - если тр.стоп недоступен в сл-нумерике убираем кнопку
        let isTsSuportedByTif = false;
        let allowTS = false;
        const route = !isNullOrUndefined(instrument) ? DataCache.Routes.getRouteByName(instrument.getRoute()) : null;
        const tifs = OrderTif;
        if (route !== null) {
            /* все тифы */
            for (const key in tifs) {
                if (route.IsAllowableTif(tifs[key], OrderType.TrailingStop)) {
                    isTsSuportedByTif = true;
                    break;
                }
            }
        }
        if (!isNullOrUndefined(account)) {
            allowTS =
                ((isTsSuportedByTif &&
                    IsAllowed.IsTradingAllowed(account, instrument, OrderType.TrailingStop).Allowed) ||
                    DataCache.isAllowedForAccount(RulesSet.FUNCTION_DONT_USE_ROUTE_SETTING_FOR_CLOSE_TS_ORDER, account)) &&
                    DataCache.isAllowedForAccount(RulesSet.FUNCTION_TRAILING_STOP, account);
            // && IsAllowableTSForRoute();
        }
        else {
            allowTS =
                (isTsSuportedByTif &&
                    IsAllowed.IsTradingAllowed(account, instrument, OrderType.TrailingStop).Allowed) ||
                    DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_DONT_USE_ROUTE_SETTING_FOR_CLOSE_TS_ORDER);
        } // && IsAllowableTSForRoute()
        return allowTS && isTsSuportedByTif
            ? IsAllowedResponce.AllowedResponce()
            : IsAllowedResponce.NotAllowedResponce();
    }
    /// <summary>
    /// Is Protective Orders Allowed 107651
    /// </summary>
    static IsProtectiveOrders() {
        return DataCache.isAllowedFeatureFlag(RulesSet.PROTECTIVE_ORDERS);
    }
    static IsUserCommentAllowed() {
        return DataCache.isAllowedForMyUser(RulesSet.FUNCTION_CARE_ORDERS);
    }
    /// <summary>
    /// Check instrument/route visibility for account
    /// </summary>
    static Check_Instrument_For_Account_Visibility(accs, ins) {
        if (ins == null || accs == null) {
            return true;
        }
        let insRouteVisibility = false;
        for (let i = 0; i < accs.length; i++) {
            insRouteVisibility = IsAllowed.CheckVisibilityForAccount(accs[i], ins) || insRouteVisibility;
        }
        return insRouteVisibility;
    }
    /// <summary>
    /// Check if instrument/route visible for account (RulesSet.VISIBILITY_ROUTE)
    /// </summary>
    static CheckVisibilityForAccount(acc, instrument) {
        if (acc == null || !instrument) {
            return true;
        }
        if (!instrument.Route) {
            // аггрегатор не торговый по определению
            return false;
        }
        const routesVisibilityRule = DataCache.getRuleStringValueForAccount(RulesSet.VISIBILITY_ROUTE, acc, null);
        if (!routesVisibilityRule) {
            return false;
        }
        if (routesVisibilityRule != RulesSet.ALLOWED_FOR_ALL) {
            // String.Empty - допустимое значение для рула, нельзя игнорить
            // ! старые(ПТ2) сервера шлют через ',', новые(ПТ3) через '|'
            const routes = routesVisibilityRule.split('|');
            const r = DataCache.Routes.getRouteByName(instrument.Route);
            if (r != null) {
                const routeID = r.RouteId.toString();
                if (routes.indexOf(routeID) === -1) {
                    return false;
                }
            }
        }
        if (!IsAllowed.CheckVisibilityForAccountInstrType(acc, instrument.TypeId)) {
            return false;
        }
        return true;
    }
    static CheckVisibilityForAccountInstrType(acc, TypeId) {
        const instrTypeVisibilityRule = DataCache.getRuleStringValueForAccount(RulesSet.VISIBILITY_INSTRUMENT_TYPE, acc, null);
        if (instrTypeVisibilityRule == null) {
            return false;
        }
        if (instrTypeVisibilityRule != RulesSet.ALLOWED_FOR_ALL) {
            // String.Empty - допустимое значение для рула, нельзя игнорить
            // ! старые(ПТ2) сервера шлют через ',', новые(ПТ3) через '|'
            const instrTypes = instrTypeVisibilityRule.split('|');
            const typeId = TypeId.toString();
            if (instrTypes.indexOf(typeId) === -1) {
                return false;
            }
        }
        return true;
    }
    static Check_FUNCTION_TRADING_BY_ACCOUNT_Rule(acc) {
        return (acc !== null &&
            DataCache.isAllowedForAccount(RulesSet.FUNCTION_TRADING, acc) &&
            (acc.AccountTradeStatus === AccountTradeStatus.ACTIVE_STATUS ||
                acc.AccountTradeStatus === AccountTradeStatus.LIQUIDATION_ONLY_STATUS ||
                acc.AccountTradeStatus === AccountTradeStatus.FINRA_DAY_TRADER_PATTERN_STATUS));
    }
    static Check_FUNCTION_SELF_TRADING_Rule(acc) {
        const allowed = DataCache.isAllowedForAccount(RulesSet.SELF_TRADING, acc);
        if (allowed) {
            return true;
        }
        return false;
    }
    /// <summary>
    /// Analyze instrument trading mode: open or closed or tradingHalt
    /// </summary>
    static Check_Instrument_TradingMode(ins, forCanceling) {
        if (isNullOrUndefined(ins)) {
            return IsAllowedResponce.AllowedResponce();
        } // ??
        switch (ins.TradingMode) {
            case TradingMode.FullyOpen:
            case TradingMode.LiquidationOnly:
                return IsAllowedResponce.AllowedResponce();
            case TradingMode.TradingHalt:
                return forCanceling
                    ? IsAllowedResponce.AllowedResponce()
                    : IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentTradingMode);
            case TradingMode.CAMaintenance:
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentTradingModeCAMaintenance);
            default:
                return IsAllowedResponce.NotAllowedResponce(IsAllowedResponceReason.InstrumentTradingMode);
        }
    }
    /// <summary>
    /// Analyze instrument delivery method and account type
    /// </summary>
    static Check_Instrument_For_Account_Delivery(instrument, account) {
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
            return true;
        }
        if ((instrument.DeliveryMethod === Instrument.Delivery_Physical &&
            account.AccountType !== AccountType.MultiAsset) ||
            (instrument.DeliveryMethod === Instrument.Delivery_Cash && account.AccountType === AccountType.MultiAsset)) {
            return false;
        }
        return true;
    }
    /// <summary>
    /// Analyze instrument trading mode: open or closed
    /// </summary>
    static Check_Last_Quote(ins, orderType, ignoreInvalidQuoteForMarketOrder /* = true */) {
        if (ins == null /* || ins.InstrType == Instrument.OPTIONS */) {
            return true;
        }
        if (orderType === OrderType.TrailingStop && ins.LastQuote == null) {
            return false;
        }
        if (orderType === OrderType.Market && !ignoreInvalidQuoteForMarketOrder && ins.LastQuote == null) {
            return false;
        }
        return true;
    }
    /// <summary>
    ///
    /// </summary>
    static Check_Session_OrderType(ins, lOrderType) {
        if (ins == null) {
            // для портфолио сессии не проверяем, или надо по каждому подинструменту
            return true;
        }
        return ins.Check_Session_OrderType(lOrderType);
    }
    static CheckOffMarket(account, instrument) {
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument)) {
            return false;
        }
        if (account.isAllowOTHTrading(instrument)) {
            return true;
        }
        if (account.DataCache.isAllowedForAccount(RulesSet.FUNCTION_TRADING_OUT_OF_SESSION, account)) {
            return true;
        }
        return false;
    }
    static Check_Session_Operation(account, instrument, operation) {
        const allowedOperations = Instrument.GetAllowedOperations(instrument);
        return (allowedOperations & operation) === operation || IsAllowed.CheckOffMarket(account, instrument);
    }
    static Check_RouteRunning(account, instrument) {
        if (isNullOrUndefined(instrument)) {
            return false;
        }
        const dc = instrument.DataCache;
        const route = dc.Routes.getRouteByName(instrument.getRoute());
        const isAllowed = dc.RulesCache.IsAllowedFeatureFlag(RulesSet.AVAILABLE_ORDER_PLACEMENT_WHEN_ROUTE_ACTIVE_AND_NOT_RUNNING);
        const routeStatus = (isAllowed && (route === null || route === void 0 ? void 0 : route.IsActive)) || (!isAllowed && (route === null || route === void 0 ? void 0 : route.IsRunning));
        return !!routeStatus || IsAllowed.CheckOffMarket(account, instrument);
    }
    static LocalizationKeyIsAllowedResponceReason(isAllowedResponce) {
        if (!(isAllowedResponce === null || isAllowedResponce === void 0 ? void 0 : isAllowedResponce.Reason)) {
            return '';
        }
        const reasonName = IsAllowedResponceReasonName[isAllowedResponce.Reason];
        return reasonName ? `IsAllowedResponceReason.${reasonName}` : '';
    }
    static LocalizeIsAllowedResponceReason(isAllowedResponce) {
        // from IsAllowedResponce.NotAllowedResponce(reason) to localized text
        const localizetionKey = IsAllowed.LocalizationKeyIsAllowedResponceReason(isAllowedResponce);
        return localizetionKey ? Resources.getResource(localizetionKey) : '';
    }
    /// <summary>
    /// Проверить доступность модификации Product type позиции
    /// </summary>
    static IsModifyProductTypeAllowed(position) {
        if (RiskPlan.GetAvailableProductTypes(position.Account, position.Instrument).length >= 2 &&
            DataCache.isAllowedForMyUser(RulesSet.FUNCTION_TRADING_MODE_MODIFICATION) &&
            position.Instrument.TradingPositionType === TradingPositionType.OnePosition) {
            return IsAllowedResponce.AllowedResponce();
        }
        else {
            return IsAllowedResponce.NotAllowedResponce();
        }
    }
    static MaxOrderAmountHasBeenReached(account, instrument, quantity, productType) {
        if (!account || !instrument || !quantity) {
            return IsAllowedResponce.NotAllowedResponce();
        }
        if (account.MaxOrderAmount < 0) {
            return IsAllowedResponce.AllowedResponce();
        }
        let maxOrderAmount = account.MaxOrderAmount;
        if (quantity.inLots) {
            maxOrderAmount = maxOrderAmount / instrument.LotSize;
        }
        if (maxOrderAmount >= quantity.value) {
            return IsAllowedResponce.AllowedResponce();
        }
        const notAllowed = IsAllowedResponce.NotAllowedResponce();
        notAllowed.ReasonText =
            Resources.getResource('Web.Mobile.advancedOE.OEButtonDisabled.more') +
                InstrumentUtils.formatAmountValue(maxOrderAmount, instrument, account, productType);
        return notAllowed;
    }
    static IsMaxLotHasBeenReached(position, qtyToClose = undefined) {
        if (!position) {
            return false;
        }
        if (!position.DataCache.getValidateMaxLotCloseOrderBoolRule()) {
            return false;
        }
        const maxLot = position.Instrument.getMaxLot(position.ProductType, position.Account);
        const amount = qtyToClose || position.Amount;
        return amount > maxLot;
    }
    static IsCashTradingAllowed(account, instrument, orderType, productType) {
        const isCashAvailableByRule = instrument === null || instrument === void 0 ? void 0 : instrument.RiskSettings.IsCashTradingAllowed(productType, account);
        const isCashAvailableByOrderType = orderType !== OrderType.OCO && orderType !== OrderType.Manual;
        return isCashAvailableByRule && isCashAvailableByOrderType;
    }
    static IsCopyTradingAllowed( /* account: Account */) {
        return DataCache.isAllowedForMyUser(RulesSet.FUNCTION_COPY_TRADE);
    }
    static IsUseSpreadPlanForCrossRates() {
        return DataCache.isAllowedForMyUser(RulesSet.FUNCTION_USE_SPREAD_PLAN_FOR_CROSS_RATES);
    }
    static Check_Session_BlockTrading(instrument) {
        return !isNullOrUndefined(instrument) && !instrument.Session.BlockTrading;
    }
    static Check_Session_CurrentPeriod(order) {
        return (!isNullOrUndefined(order.Instrument) &&
            (order.Instrument.Session.CurrentSessionPeriod !== null || order.Status === OrderStatus.OFF_MARKET));
    }
    static IsAggregatedPriceAllowed() {
        return DataCache.isAllowedForMyUser(RulesSet.FUNCTION_AGGREGATED_PRICE);
    }
}
//
// прриходят с консоли enum IsAllowedRule in WebConsole.core.IsAllowedWEB
//
// IsAllowed.RequestsTypes =
// {
//     //PositionModifying = 1,
//     //OrderModifying = 2,
//     //ClosePosition = 3,
//     //ReversePosition = 4,
//     //MutualClose = 5,
//     //OrderExecuting = 6,
//     //OrderCancelling = 7,
//     //IsTradingAllowed = 8,
//     //IsAllowSLTP = 9,
//     //IsAllowStopLimit = 10,
//     //IsAllowStop = 11,
//     //IsAllowLimit = 12,
//     //ReverseByInstrument = 13,
//     //CancellAll = 14,
//     //IsTralingStopAllow = 15,
//     //IsOCOAllowed = 16,
//     //IsRollbackAllowed = 17,
//     //IsCloseAllPositionsAllowed = 18,
//     //IsCloseAllOrdersAllowed = 19,
//     //IsCloseAllAllowed_True_False = 20,
//     //isAllowed_TERMINAL_HTML = 21,
//     //isAllowedForMyUser_FUNCTION_FIFO_POSITIONS = 22,
//     //isAllowedForMyUser_FUNCTION_CHART_VOLUME_BARS = 23,
//     //isAllowed_FUNCTION_CLOSE_POSITIONS_DISABLE = 24,
//     //isAllowed_FUNCTION_MAM = 25,
//     //isAllowedForMyUser_FUNCTION_POSITIONSADMIN = 26,
//     //isAllowedForMyUser_FUNCTION_MUTUAL_CLOSE = 27,
//     //isAllowed_FUNCTION_MUTUAL_CLOSE = 28,
//     //isAllowedForMyUser_FUNCTION_SLTP = 29,
//     //isAllowed_FUNCTION_NEWS=30
// };
export class IsAllowedResponce {
    constructor() {
        this.Allowed = false;
        this.Reason = undefined;
        this.ReasonLocalizationKey = '';
        this.ReasonText = '';
    }
    static AllowedResponce() {
        const res = new IsAllowedResponce();
        res.Allowed = true;
        return res;
    }
    static NotAllowedResponce(reason = IsAllowedResponceReason.Unknown) {
        const res = new IsAllowedResponce();
        res.Allowed = false;
        res.Reason = reason;
        res.ReasonLocalizationKey = IsAllowed.LocalizationKeyIsAllowedResponceReason(res);
        res.ReasonText = IsAllowed.LocalizeIsAllowedResponceReason(res);
        // TODO
        // if (reason != IsAllowedResponceReason.Unknown)
        //    IsAllowed.SetReason(res.ReasonText);
        return res;
    }
}
